// Shared trial-status helper. Used server-side (chat, routines,
// whispers, cron) and client-side (AuthProvider, LapsedBanner).
//
// Lapsed = seat_type is 'trial' AND the 14-day window has elapsed.
// Primary source: profiles.trial_ends_at (set at signup, backfilled
// for existing rows in Session 46 Phase A). Falls back to
// trial_started_at || created_at for rows missing trial_ends_at
// during the migration transition.
//
// Owner / founder roles and non-trial seat_types never considered
// lapsed. 'free' kept in the seat list as a defensive alias during
// migration — Phase A0 SQL converts 'guest'/'founder' seat_types to
// override='special' + seat_type='trial', and A1 narrows the CHECK
// constraint so 'free' rows shouldn't exist post-migration.

export const TRIAL_DURATION_DAYS = 14;
export const RETRIAL_COOLDOWN_DAYS = 90;

export function isLapsedTrial(profile) {
  if (!profile) return false;
  if (profile.role === "owner" || profile.role === "founder") return false;
  // Owner override (e.g. 'special') grants access regardless of trial state.
  // Mirrors the owner/founder role bypass above. 'banned' (86) carve-out:
  // banned users stay locked out — the override doesn't unlock them.
  if (profile.seat_type_override && profile.seat_type_override !== "banned") return false;
  const seat = profile.seat_type;
  if (!seat || !["trial", "free"].includes(seat)) return false;
  // Primary source: trial_ends_at (Session 46 canonical).
  if (profile.trial_ends_at) {
    return new Date(profile.trial_ends_at) < new Date();
  }
  // Backward compat for un-backfilled rows.
  const started = profile.trial_started_at || profile.created_at;
  if (!started) return false;
  const days = (Date.now() - new Date(started).getTime()) / 86400000;
  return days > TRIAL_DURATION_DAYS;
}

// True when a lapsed trial user is due an automatic retrial —
// 90+ days since trial_ends_at AND no prior retrial on record.
// Session 46 login path fires the reset when this returns true.
export function isRetrialEligible(profile) {
  if (!profile) return false;
  if (profile.role === "owner" || profile.role === "founder") return false;
  if (profile.retrialed_at) return false;
  if (profile.seat_type !== "trial" && profile.seat_type !== "free") return false;
  if (!profile.trial_ends_at) return false;
  const cutoff = Date.now() - RETRIAL_COOLDOWN_DAYS * 86400000;
  return new Date(profile.trial_ends_at).getTime() < cutoff;
}

// Standard 402 payload shape returned by any server route that
// blocks a lapsed user. Frontend checks res.status === 402 and
// body.paywall === true to render the upsell state.
export function paywallResponse() {
  return Response.json({
    paywall: true,
    paywallType: "trial_expired",
    title: "Fülkit's AI side is on pause",
    message: "Everything's saved. $9 flips it back on.",
    upgradeUrl: "/settings/billing",
    plans: [
      { key: "standard", label: "Standard", price: "$9/mo", messages: 450 },
      { key: "pro", label: "Pro", price: "$15/mo", messages: 800 },
    ],
  }, { status: 402 });
}
